import React, { createContext, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';

import { useDepositBonusContext } from 'src/components/ClientProfile/PaymentTab/Deposit/DepositBonus';

export const PaymentContext = createContext();

const noop = () => {};

/**
 * This provider establishes a unified interface for amount input components,
 * enabling the reuse of a single UI component (DepositField) for both Deposit and Withdrawal pages.
 *
 * Implementation details:
 * 1. In the Deposit scenario, the input component relies on `setDepositAmount`, provided via `useDepositBonusContext`.
 * 2. In the Withdrawal scenario, `useDepositBonusContext` is not present.
 *
 * To prevent errors during Withdrawal, we check the result of `useDepositBonusContext`:
 * - If it exists (Deposit) -> we pass the actual `setDepositAmount`.
 * - If it is missing (Withdrawal) -> we use a no-op function (noop).
 */

const getSafeNumber = value => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

export const PaymentProvider = ({ children, selectedPayment }) => {
  const bonusContext = useDepositBonusContext();
  const minAmount = getSafeNumber(selectedPayment?.minAmount);
  const maxAmount = getSafeNumber(selectedPayment?.maxAmount);

  const state = useMemo(
    () => ({
      maxAmount,
      minAmount,
      setDepositAmount: bonusContext ? bonusContext.setDepositAmount : noop,
    }),
    [maxAmount, minAmount, bonusContext],
  );

  return <PaymentContext.Provider value={state}>{children}</PaymentContext.Provider>;
};

export const usePaymentContext = () => useContext(PaymentContext);

PaymentProvider.propTypes = {
  children: PropTypes.node.isRequired,
  selectedPayment: PropTypes.shape({
    maxAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    minAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }).isRequired,
};
